import Choices from 'choices.js';
// choices.js ships its own stylesheet; the rest of the site doesn't use
// choices.js for its selects (see module/select.js for the "niceselect"
// pattern), so its CSS isn't loaded anywhere else. Import it here so
// Vite bundles it into main.css alongside our other styles.
import 'choices.js/public/assets/styles/choices.min.css';

// Self-guard: bail cheaply on pages without the table.
const container = document.querySelector('[data-property-upgrades]');
if (container) {
    initPropertyUpgrades(container);
}

function initPropertyUpgrades(root) {
    const filterRoot = root.querySelector('[data-property-upgrades-filters]');
    const counterRoot = root.querySelector('[data-property-upgrades-counter]');
    const counterVisibleEl = root.querySelector('[data-property-upgrades-visible]');
    const counterTotalEl = root.querySelector('[data-property-upgrades-total]');
    const tbody = root.querySelector('[data-property-upgrades-tbody]');
    const tableWrap = root.querySelector('[data-property-upgrades-table-wrap]');
    const emptyFilters = root.querySelector('[data-property-upgrades-empty-filters]');
    const clearBtn = root.querySelector('[data-property-upgrades-clear]');

    const categorySelect = root.querySelector('[data-property-upgrades-filter="category"]');
    const yearSelect = root.querySelector('[data-property-upgrades-filter="year"]');
    const upgradeTypeSelect = root.querySelector('[data-property-upgrades-filter="upgradeType"]');
    const searchInput = root.querySelector('[data-property-upgrades-filter="search"]');

    if (!tbody || !categorySelect || !yearSelect || !upgradeTypeSelect || !searchInput) {
        return;
    }

    const rows = Array.prototype.slice.call(tbody.querySelectorAll('[data-property-upgrades-row]'));
    const totalRows = rows.length;

    if (counterTotalEl) {
        counterTotalEl.textContent = String(totalRows);
    }

    // If the DB returned zero rows, the server emitted the "no data" empty
    // state and hid the filter bar. Nothing for us to do.
    if (totalRows === 0) {
        return;
    }

    // Preserve the full Upgrade Type option list so we can restore it when
    // Category clears.
    const fullUpgradeTypeOptions = [];
    for (let i = 0; i < upgradeTypeSelect.options.length; i++) {
        const opt = upgradeTypeSelect.options[i];
        fullUpgradeTypeOptions.push({ value: opt.value, label: opt.text });
    }

    // Initialise choices.js on the three filter selects. Matches the
    // house-style config in site/scripts/module/dropdown.js.
    const choicesConfig = {
        position: 'bottom',
        searchEnabled: false,
        shouldSort: false,
        allowHTML: false,
        itemSelectText: '',
    };

    const categoryChoices = new Choices(categorySelect, choicesConfig);
    const yearChoices = new Choices(yearSelect, choicesConfig);
    const upgradeTypeChoices = new Choices(upgradeTypeSelect, choicesConfig);

    // Debounce helper (matches the plain-vanilla style elsewhere in /module/).
    function debounce(fn, wait) {
        let t = null;
        return function debounced() {
            const args = arguments;
            const ctx = this;
            if (t) {
                clearTimeout(t);
            }
            t = setTimeout(function () {
                t = null;
                fn.apply(ctx, args);
            }, wait);
        };
    }

    // ---- URL state sync --------------------------------------------------

    function readUrlState() {
        const params = new URLSearchParams(window.location.search);
        return {
            category: params.get('category') || '',
            year: params.get('year') || '',
            upgradeType: params.get('type') || '',
            q: params.get('q') || '',
        };
    }

    function writeUrlState(state) {
        const params = new URLSearchParams();
        if (state.category) params.set('category', state.category);
        if (state.year) params.set('year', state.year);
        if (state.upgradeType) params.set('type', state.upgradeType);
        if (state.q) params.set('q', state.q);

        const qs = params.toString();
        const newUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
        window.history.replaceState(null, '', newUrl);
    }

    function currentState() {
        return {
            category: categorySelect.value || '',
            year: yearSelect.value || '',
            upgradeType: upgradeTypeSelect.value || '',
            q: (searchInput.value || '').trim(),
        };
    }

    // ---- Filtering core --------------------------------------------------

    function applyFilters() {
        const state = currentState();
        const needle = state.q.toLowerCase();
        const hasActiveFilters = Boolean(
            state.category ||
            state.year ||
            state.upgradeType ||
            needle.length > 0
        );

        let visibleCount = 0;

        if (!hasActiveFilters) {
            for (let i = 0; i < rows.length; i++) {
                rows[i].hidden = true;
            }
            if (counterVisibleEl) {
                counterVisibleEl.textContent = '0';
            }
            if (counterRoot) {
                counterRoot.hidden = true;
            }
            if (emptyFilters) {
                emptyFilters.hidden = true;
            }
            if (tableWrap) {
                tableWrap.hidden = true;
            }
            writeUrlState(state);
            return;
        }

        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const rowCategory = row.getAttribute('data-category') || '';
            const rowYear = row.getAttribute('data-year') || '';
            const rowUpgradeType = row.getAttribute('data-upgrade-type') || '';
            const rowSearch = row.getAttribute('data-search') || '';

            let match = true;
            if (state.category && rowCategory !== state.category) match = false;
            if (match && state.year && rowYear !== state.year) match = false;
            if (match && state.upgradeType && rowUpgradeType !== state.upgradeType) match = false;
            if (match && needle && rowSearch.indexOf(needle) === -1) match = false;

            row.hidden = !match;
            if (match) visibleCount++;
        }

        // Update counter + empty-state toggles.
        if (counterVisibleEl) {
            counterVisibleEl.textContent = String(visibleCount);
        }
        if (counterRoot) {
            counterRoot.hidden = false;
        }
        if (emptyFilters) {
            emptyFilters.hidden = visibleCount !== 0;
        }
        if (tableWrap) {
            // Keep table wrapper mounted when empty so <thead> can reappear
            // instantly when the filters relax; hiding the <tbody> alone
            // produces an odd floating header.
            tableWrap.hidden = visibleCount === 0;
        }

        writeUrlState(state);
    }

    // ---- Upgrade Type scoping to current Category ------------------------

    function scopeUpgradeTypeToCategory() {
        const category = categorySelect.value || '';
        const previousUpgradeType = upgradeTypeSelect.value || '';

        let allowed;
        if (!category) {
            allowed = fullUpgradeTypeOptions.slice();
        } else {
            const seen = Object.create(null);
            allowed = [{ value: '', label: fullUpgradeTypeOptions[0].label }];
            for (let i = 0; i < rows.length; i++) {
                const row = rows[i];
                if ((row.getAttribute('data-category') || '') !== category) continue;
                const t = row.getAttribute('data-upgrade-type') || '';
                if (!t || seen[t]) continue;
                seen[t] = true;
                allowed.push({ value: t, label: t });
            }
        }

        // Rebuild Upgrade Type options via choices.js. Mark the empty-value
        // option as a placeholder so it renders greyed-out like the
        // Category and Year selects (which get this treatment for free from
        // choices.js's native-init path).
        upgradeTypeChoices.clearStore();
        upgradeTypeChoices.setChoices(
            allowed.map(function (o, idx) {
                return {
                    value: o.value,
                    label: o.label,
                    selected: idx === 0,
                    disabled: false,
                    placeholder: o.value === '',
                };
            }),
            'value',
            'label',
            true
        );

        // If the previously-selected upgrade type is no longer valid, clear it.
        let stillValid = false;
        for (let i = 0; i < allowed.length; i++) {
            if (allowed[i].value === previousUpgradeType && previousUpgradeType !== '') {
                stillValid = true;
                break;
            }
        }
        if (stillValid) {
            upgradeTypeChoices.setChoiceByValue(previousUpgradeType);
        }
    }

    // ---- Event wiring ----------------------------------------------------

    categorySelect.addEventListener('change', function () {
        scopeUpgradeTypeToCategory();
        applyFilters();
    });
    yearSelect.addEventListener('change', applyFilters);
    upgradeTypeSelect.addEventListener('change', applyFilters);

    const debouncedApply = debounce(applyFilters, 150);
    searchInput.addEventListener('input', debouncedApply);

    if (clearBtn) {
        clearBtn.addEventListener('click', function (e) {
            e.preventDefault();
            clearAllFilters();
        });
    }

    function clearAllFilters() {
        // Reset category first so Upgrade Type scope widens back to full.
        categoryChoices.setChoiceByValue('');
        yearChoices.setChoiceByValue('');
        searchInput.value = '';
        scopeUpgradeTypeToCategory();
        // Belt-and-braces: clear any stale upgrade type selection.
        upgradeTypeChoices.setChoiceByValue('');
        applyFilters();
    }

    // ---- Seed from URL then render --------------------------------------

    function seedFromUrl() {
        const state = readUrlState();

        if (state.category) {
            categoryChoices.setChoiceByValue(state.category);
        }
        if (state.year) {
            yearChoices.setChoiceByValue(state.year);
        }

        // Category-scoped Upgrade Type options must be computed BEFORE we
        // try to set the upgrade type from URL — otherwise a deep-link
        // combining category=boilers&type=Bathroom would silently fail.
        scopeUpgradeTypeToCategory();

        if (state.upgradeType) {
            // setChoiceByValue is a no-op if the option isn't present in
            // the currently scoped list — exactly the behaviour we want
            // for the "deep-link with stale type" case.
            upgradeTypeChoices.setChoiceByValue(state.upgradeType);
        }

        if (state.q) {
            searchInput.value = state.q;
        }
    }

    seedFromUrl();
    applyFilters();
}
